/**
 * useDocumentTitle.ts
 *
 * Composable for managing document title with environment-specific suffixes.
 * Adds "DEV" suffix for local development environments.
 * Supports notification titles that can be cleared on user activity.
 */

import { ref } from "vue";
import { useIsLocalEnv } from "@/composables/useIsLocalEnv";

// Shared env detection — single source of truth so the "DEV" title suffix
// also disappears under VITE_SIMULATE_PROD when docs screenshots are run.
const { isLocalEnv } = useIsLocalEnv();

// Track if there's an active notification in the title
const hasNotificationTitle = ref(false);

// Store the last known "base" title before notification was set
let lastBaseTitle = "";

/**
 * Sets the document title with environment-specific suffix
 * @param baseTitle - The base title without environment suffix
 */
function setDocumentTitle(baseTitle: string): void {
  const suffix = isLocalEnv.value ? " DEV" : "";
  const fullTitle = baseTitle.includes("ToastHost")
    ? baseTitle.replace("ToastHost", `ToastHost${suffix}`)
    : baseTitle.includes("Toasthost")
      ? baseTitle.replace("Toasthost", `Toasthost${suffix}`)
      : `${baseTitle}${suffix}`;

  document.title = fullTitle;
}

/**
 * Sets a notification title (e.g., "New message from X")
 * This can be cleared later by resetDocumentTitle
 * @param notificationTitle - The notification title to show
 */
function setNotificationTitle(notificationTitle: string): void {
  // Store the current title before setting notification (if not already a notification)
  if (!hasNotificationTitle.value) {
    lastBaseTitle = document.title;
  }
  hasNotificationTitle.value = true;
  setDocumentTitle(notificationTitle);
}

/**
 * Resets the document title to the base title, clearing any notification
 */
function resetDocumentTitle(): void {
  if (hasNotificationTitle.value) {
    hasNotificationTitle.value = false;
    if (lastBaseTitle) {
      document.title = lastBaseTitle;
    } else {
      setDocumentTitle(getBaseTitle());
    }
  }
}

/**
 * Checks if there's currently a notification title displayed
 * @returns True if a notification title is currently shown
 */
function hasActiveNotification(): boolean {
  return hasNotificationTitle.value;
}

/**
 * Gets the current base title (without environment suffix)
 * @returns The base site title "Toasthost" or "Toasthost DEV" depending on environment
 */
function getBaseTitle(): string {
  return isLocalEnv.value ? "Toasthost DEV" : "Toasthost";
}

/**
 * Composable function that provides title management utilities
 */
export function useDocumentTitle() {
  return {
    setDocumentTitle,
    setNotificationTitle,
    resetDocumentTitle,
    hasActiveNotification,
    getBaseTitle,
    isLocalEnv,
  };
}
